// ABOUTME: One store for license purchase in a collection (DVLP-7860): the offer shelf that
// ABOUTME: the prompt and every course card read, and the Choose > Pay > Processing flow.
import { warn } from '@execonline-inc/logging';
import { find } from '@execonline-inc/collections';
import { just, Maybe, nothing } from 'maybeasy';
import { action, computed, makeObservable, observable } from 'mobx';
import { callApi } from '../../../../Appy';
import { startExperienceDetailsRoute } from '../../../../ClientRoutes';
import history from '../../../../History';
import { Link } from '../../../../Resource/Types';
import { paymentIntentResourceDecoder } from '../../DiscoveryPortal/Enrollment/Store/Decoders/PaymentDecoder';
import { PaymentIntentResource } from '../../DiscoveryPortal/Enrollment/Store/Types';
import {
  createPaymentIntentStatusRequest,
  setPaymentStateProcessing,
} from '../ProgramExperienceEnrollment/ExperiencePaymentContent/Common/PaymentWizardReactions/Helpers';
import { LicenseCommerceType } from '../Experience/Types';
import { licenseOffersResourceDecoder } from '../LicenseOffers/Decoders';
import { LicenseOffer } from '../LicenseOffers/Types';
import { loadAndValidateStripe } from '../Stripe';
import { Entry, FlowState, OpenIntent, ShelfState } from './Types';

/** Same waits as the course checkout: Pusher first, then ask the server, then stop. */
const WAIT_MS = 15_000;

const offersLink = (invitationUuid: string): Link => ({
  rel: 'self',
  href: `/aep/${invitationUuid}/license_offers.json`,
  method: 'get',
  type: 'application/json',
});

const purchaseLinkOf = (offer: LicenseOffer): Maybe<Link> =>
  find((link: Link) => link.rel === 'purchase', offer.links);

class LicensePurchaseStore {
  @observable shelf: ShelfState = { kind: 'not-selling' };
  @observable flow: FlowState = { kind: 'closed' };
  @observable promptDismissed = false;

  // In memory only, on purpose: Dismiss hides the prompt while they move around the
  // portal, and a page load brings it back. It is the one place that says a single
  // license opens all of these courses, so it returns until they hold one.
  private dismissedInvitations = new Set<string>();
  private invitationUuid: Maybe<string> = nothing();
  private openIntent: Maybe<OpenIntent> = nothing();
  private timer: Maybe<ReturnType<typeof setTimeout>> = nothing();

  constructor() {
    makeObservable(this);
  }

  // ---- the shelf ----------------------------------------------------------

  /**
   * Loads the offers once per collection. Collections not set to license purchase
   * make no request at all, so this costs nothing on the rest of the product.
   */
  @action
  load = (invitationUuid: string, licenseCommerceType: LicenseCommerceType): void => {
    // An open PaymentIntent belongs to the collection it was started in: the
    // server books it against that invitation's use case. Moving to another
    // collection in the SPA must never reuse it.
    if (!this.isCurrentInvitation(invitationUuid)) {
      this.openIntent = nothing();
    }
    this.invitationUuid = just(invitationUuid);
    this.promptDismissed = this.dismissedInvitations.has(invitationUuid);

    switch (licenseCommerceType) {
      case 'not-available': {
        this.shelf = { kind: 'not-selling' };
        return;
      }
      case 'cc-only': {
        break;
      }
    }

    this.shelf = { kind: 'loading' };
    callApi(licenseOffersResourceDecoder, {}, offersLink(invitationUuid)).fork(
      // A slow response for a collection the buyer has since left must not
      // overwrite the shelf of the one they are on.
      action((error: unknown) => {
        if (!this.isCurrentInvitation(invitationUuid)) {
          return;
        }
        warn('LicenseOffersFetchFailed', String(error));
        this.shelf = { kind: 'unavailable' };
      }),
      action((offers) => {
        if (!this.isCurrentInvitation(invitationUuid)) {
          return;
        }
        this.shelf = { kind: 'ready', offers };
      }),
    );
  };

  private isCurrentInvitation = (invitationUuid: string): boolean =>
    this.invitationUuid.map((current) => current === invitationUuid).getOrElseValue(false);

  @computed
  get offers(): LicenseOffer[] {
    switch (this.shelf.kind) {
      case 'ready': {
        return this.shelf.offers.payload.offers;
      }
      case 'not-selling':
      case 'loading':
      case 'unavailable': {
        return [];
      }
    }
  }

  @computed
  get receiptEmail(): Maybe<string> {
    switch (this.shelf.kind) {
      case 'ready': {
        return just(this.shelf.offers.payload.receiptEmail);
      }
      case 'not-selling':
      case 'loading':
      case 'unavailable': {
        return nothing();
      }
    }
  }

  @computed
  get courseCount(): number {
    switch (this.shelf.kind) {
      case 'ready': {
        return this.shelf.offers.payload.courseCount;
      }
      case 'not-selling':
      case 'loading':
      case 'unavailable': {
        return 0;
      }
    }
  }

  /** The collection prompt: nothing covers anything they can see, and something is for sale. */
  @computed
  get showsPrompt(): boolean {
    switch (this.shelf.kind) {
      case 'ready': {
        return (
          !this.promptDismissed &&
          !this.shelf.offers.payload.hasLicenseEntitlement &&
          this.offers.length > 0
        );
      }
      case 'not-selling':
      case 'loading':
      case 'unavailable': {
        return false;
      }
    }
  }

  /** A card is buyable when an offer covers its family; otherwise it stays Preview Only. */
  canBuy = (programFamilyId: number): boolean =>
    this.offers.some((offer) => offer.coveredProgramFamilyIds.includes(programFamilyId));

  offersFor = (entry: Entry): LicenseOffer[] => {
    switch (entry.kind) {
      case 'collection': {
        return this.offers;
      }
      case 'course': {
        return this.offers.filter((offer) =>
          offer.coveredProgramFamilyIds.includes(entry.programFamilyId),
        );
      }
    }
  };

  @action
  dismissPrompt = (): void => {
    this.promptDismissed = true;
    this.invitationUuid.do((uuid) => this.dismissedInvitations.add(uuid));
  };

  // ---- opening and choosing ------------------------------------------------

  @action
  openFromCollection = (): void => {
    this.open({ kind: 'collection' });
  };

  @action
  openFromCourse = (programFamilyId: number, courseTitle: string, experienceId: string): void => {
    this.open({ kind: 'course', programFamilyId, courseTitle, experienceId });
  };

  @action
  private open = (entry: Entry): void => {
    const offers = this.offersFor(entry);
    if (offers.length === 0) {
      return;
    }
    // Pre-selecting the first card would make the cheapest option the default and
    // anchor the buyer low; with several options they choose. One option is no
    // choice, so it starts selected.
    const selectedId =
      offers.length === 1
        ? find(() => true, offers).map((offer) => offer.productLicenseTypeId)
        : nothing<number>();
    this.flow = { kind: 'choosing', entry, selectedId };
  };

  @action
  select = (productLicenseTypeId: number): void => {
    switch (this.flow.kind) {
      case 'choosing': {
        this.flow = { ...this.flow, selectedId: just(productLicenseTypeId) };
        break;
      }
      case 'closed':
      case 'starting-purchase':
      case 'paying':
      case 'processing':
      case 'purchased':
      case 'failed': {
        break;
      }
    }
  };

  @computed
  get selectedOffer(): Maybe<LicenseOffer> {
    switch (this.flow.kind) {
      case 'choosing': {
        const offers = this.offers;
        return this.flow.selectedId.andThen((id) =>
          find((offer: LicenseOffer) => offer.productLicenseTypeId === id, offers),
        );
      }
      case 'starting-purchase':
      case 'paying':
      case 'processing':
      case 'purchased': {
        return just(this.flow.offer);
      }
      case 'closed':
      case 'failed': {
        return nothing();
      }
    }
  }

  // ---- paying ----------------------------------------------------------------

  /**
   * Same license as an intent already open: reuse it, no second ledger row.
   * A different license: start a new purchase; the abandoned intent is left for
   * the cleanup job.
   */
  @action
  continueToPayment = (): void => {
    if (this.flow.kind !== 'choosing') {
      return;
    }
    const entry = this.flow.entry;

    this.selectedOffer.do((offer) => {
      const reusable = this.openIntent.andThen((open) =>
        open.productLicenseTypeId === offer.productLicenseTypeId &&
        this.isCurrentInvitation(open.invitationUuid)
          ? just(open)
          : nothing<OpenIntent>(),
      );

      reusable.cata({
        Just: (open) => {
          this.flow = {
            kind: 'paying',
            entry,
            offer,
            paymentIntentResource: open.paymentIntentResource,
            stripe: open.stripe,
          };
        },
        Nothing: () => {
          this.startPurchase(entry, offer);
        },
      });
    });
  };

  @action
  private startPurchase = (entry: Entry, offer: LicenseOffer): void => {
    this.flow = { kind: 'starting-purchase', entry, offer };

    purchaseLinkOf(offer).cata({
      Nothing: () => {
        this.fail(entry, 'could-not-start', true);
      },
      Just: (link) =>
        callApi(
          paymentIntentResourceDecoder,
          { product_license_type_id: offer.productLicenseTypeId },
          link,
        )
          // The two steps fail with different error types; both are only logged.
          .mapError<unknown>((error) => error)
          .andThen((paymentIntentResource: PaymentIntentResource) =>
            loadAndValidateStripe({ paymentIntentResource })
              .mapError<unknown>((error) => error)
              .map((stripe) => ({ paymentIntentResource, stripe })),
          )
          .fork(
            action((error: unknown) => {
              warn('LicensePurchaseStartFailed', String(error));
              this.fail(entry, 'could-not-start', true);
            }),
            action(({ paymentIntentResource, stripe }) => {
              this.openIntent = this.invitationUuid.map((invitationUuid) => ({
                invitationUuid,
                productLicenseTypeId: offer.productLicenseTypeId,
                paymentIntentResource,
                stripe,
              }));
              this.flow = { kind: 'paying', entry, offer, paymentIntentResource, stripe };
            }),
          ),
    });
  };

  /** Change license / Back to licenses: keeps the current choice selected. */
  @action
  backToChoosing = (): void => {
    switch (this.flow.kind) {
      case 'paying':
      case 'starting-purchase': {
        this.flow = {
          kind: 'choosing',
          entry: this.flow.entry,
          selectedId: just(this.flow.offer.productLicenseTypeId),
        };
        break;
      }
      case 'failed': {
        if (this.flow.canGoBack) {
          this.open(this.flow.entry);
        }
        break;
      }
      case 'closed':
      case 'choosing':
      case 'processing':
      case 'purchased': {
        break;
      }
    }
  };

  // ---- after Stripe takes the card ------------------------------------------

  @action
  paymentConfirmed = (stripePaymentIntentId: string): void => {
    if (this.flow.kind !== 'paying') {
      return;
    }
    // The card is charged; this intent must never be offered for reuse again.
    this.openIntent = nothing();
    this.flow = {
      kind: 'processing',
      entry: this.flow.entry,
      offer: this.flow.offer,
      stripePaymentIntentId,
      phase: 'waiting-for-message',
    };

    setPaymentStateProcessing(stripePaymentIntentId).fork(
      (error: unknown) => {
        warn('LicensePaymentStateFailed', String(error));
      },
      () => {},
    );
    this.after(WAIT_MS, this.requestStatus);
  };

  /** Nothing over Pusher yet: ask the server to re-check the intent and publish. */
  @action
  private requestStatus = (): void => {
    if (this.flow.kind !== 'processing' || this.flow.phase !== 'waiting-for-message') {
      return;
    }
    const piId = this.flow.stripePaymentIntentId;
    this.flow = { ...this.flow, phase: 'status-requested' };

    createPaymentIntentStatusRequest(piId).fork(
      (error: unknown) => {
        warn('LicensePaymentStatusRequestFailed', String(error));
      },
      () => {},
    );
    this.after(WAIT_MS, this.giveUp);
  };

  @action
  private giveUp = (): void => {
    if (this.flow.kind !== 'processing') {
      return;
    }
    // Charged but unconfirmed. No way back: retrying could charge them twice.
    this.flow = {
      kind: 'failed',
      entry: this.flow.entry,
      canGoBack: false,
      reason: 'not-confirmed',
    };
  };

  /** From the Stripe presence channel. */
  @action
  messageReceived = (event: 'succeeded' | 'processing' | 'server-error', body: unknown): void => {
    if (this.flow.kind !== 'processing') {
      return;
    }
    switch (event) {
      case 'succeeded': {
        if (isLicensePurchaseMessage(body)) {
          this.clearTimer();
          this.flow = {
            kind: 'purchased',
            entry: this.flow.entry,
            offer: this.flow.offer,
            stripePaymentIntentId: this.flow.stripePaymentIntentId,
          };
        }
        break;
      }
      case 'processing': {
        // Stripe is still settling. Keep waiting; the timers already bound this.
        break;
      }
      case 'server-error': {
        this.clearTimer();
        this.flow = {
          kind: 'failed',
          entry: { kind: 'collection' },
          canGoBack: false,
          reason: 'server-error',
        };
        break;
      }
    }
  };

  // ---- leaving -------------------------------------------------------------

  /** The dialog's close. Ignored while a charged payment is being fulfilled. */
  @action
  close = (): void => {
    switch (this.flow.kind) {
      case 'processing': {
        return;
      }
      case 'closed':
      case 'choosing':
      case 'starting-purchase':
      case 'paying':
      case 'purchased':
      case 'failed': {
        this.clearTimer();
        this.flow = { kind: 'closed' };
      }
    }
  };

  /**
   * After a purchase the participant's browse scope, licensing statuses and the
   * prompt all change server-side, so reload rather than patch each one. They stay
   * on the page they bought from, now able to enroll.
   */
  finish = (): void => {
    globalThis.location.reload();
  };

  /**
   * Bought from a course card: open that course, where they enroll. A full load
   * rather than a client-side route change, so browse scope and licensing status
   * come back from the server with the new license in them.
   */
  @action
  goToCourse = (experienceId: string): void => {
    // The course page's route id ("program-30"). A bare number there means a
    // UseCasesProgramFamily row, so a family id alone would open the wrong page.
    this.invitationUuid.do((uuid) => {
      history.push(startExperienceDetailsRoute(uuid, experienceId));
      globalThis.location.reload();
    });
  };

  // ---- internals -------------------------------------------------------------

  @action
  private fail = (entry: Entry, reason: 'could-not-start', canGoBack: boolean): void => {
    this.flow = { kind: 'failed', entry, canGoBack, reason };
  };

  private after = (ms: number, fn: () => void): void => {
    this.clearTimer();
    this.timer = just(setTimeout(fn, ms));
  };

  private clearTimer = (): void => {
    this.timer.do(clearTimeout);
    this.timer = nothing();
  };
}

/** Fulfillment publishes { kind: 'license-purchase', payload: {...} } on the Stripe channel. */
const isLicensePurchaseMessage = (body: unknown): boolean =>
  typeof body === 'object' &&
  body !== null &&
  (body as { kind?: unknown }).kind === 'license-purchase';

export type { LicensePurchaseStore };

const licensePurchaseStore = new LicensePurchaseStore();
export default licensePurchaseStore;
